import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Store, Phone, MapPin, Search, Lock, ArrowLeft, Wrench, Delete, CheckCircle, Clock, AlertCircle, Package, UserPlus, Mail, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const GY = { green: '#009b3a', yellow: '#fedd00', red: '#ef3340' };

// Backend-provided (French) status values -> color/icon. Keyed on raw backend data; do not translate the keys.
const STATUS_MAP = {
  'En attente': { color: '#f59e0b', icon: Clock },
  'En cours': { color: '#3b82f6', icon: Wrench },
  'Terminé': { color: '#10b981', icon: CheckCircle },
  'Livré': { color: '#8b5cf6', icon: Package },
  'Annulé': { color: '#ef4444', icon: AlertCircle },
};

// Maps the raw backend status value to its translation key (display label only).
const STATUS_LABEL_KEY = {
  'En attente': 'storefront.status.waiting',
  'En cours': 'storefront.status.in_progress',
  'Terminé': 'storefront.status.done',
  'Livré': 'storefront.status.delivered',
  'Annulé': 'storefront.status.cancelled',
};

const CompanyStorefrontPage = () => {
  const { t } = useLanguage();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState('track'); // 'track' | 'login'

  // Track repair
  const [trackRef, setTrackRef] = useState('');
  const [trackResult, setTrackResult] = useState(null);
  const [trackError, setTrackError] = useState('');
  const [trackLoading, setTrackLoading] = useState(false);

  // Client login
  const [telephone, setTelephone] = useState('');
  const [pinDigits, setPinDigits] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Client registration
  const [showRegister, setShowRegister] = useState(false);
  const [regNom, setRegNom] = useState('');
  const [regTel, setRegTel] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPin, setRegPin] = useState('');
  const [regPin2, setRegPin2] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/public/company/${slug}`)
      .then(r => setCompany(r.data))
      .catch(() => setNotFound(true));
    // Auto-track if ref is in URL params
    const params = new URLSearchParams(window.location.search);
    const refParam = params.get('ref');
    if (refParam) {
      setTrackRef(refParam.toUpperCase());
      setTab('track');
      axios.get(`${API}/api/public/company/${slug}/track/${refParam.trim()}`)
        .then(res => setTrackResult(res.data))
        .catch(err => setTrackError(err.response?.data?.detail || t('storefront.track.not_found')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!trackRef.trim()) return;
    setTrackError('');
    setTrackResult(null);
    setTrackLoading(true);
    try {
      const res = await axios.get(`${API}/api/public/company/${slug}/track/${trackRef.trim()}`);
      setTrackResult(res.data);
    } catch (err) {
      setTrackError(err.response?.data?.detail || t('storefront.track.not_found'));
    }
    setTrackLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!telephone || !(pinDigits.length === 4 || pinDigits.length === 6)) {
      setLoginError(t('storefront.login.error_fields'));
      return;
    }
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/api/public/company/${slug}/client-login`, { telephone, pin: pinDigits });
      localStorage.setItem('token', res.data.access_token);
      window.location.href = '/espace-client';
    } catch (err) {
      setLoginError(err.response?.data?.detail || t('storefront.login.error_invalid'));
    }
    setLoginLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regNom.trim() || !regTel.trim()) { setRegError(t('storefront.register.error_required')); return; }
    if (regPin.length < 4) { setRegError(t('storefront.register.error_pin_short')); return; }
    if (regPin !== regPin2) { setRegError(t('storefront.register.error_pin_mismatch')); return; }
    setRegError(''); setRegLoading(true);
    try {
      const res = await axios.post(`${API}/api/public/company/${slug}/client-register`, {
        nom: regNom.trim(), telephone: regTel.trim(), email: regEmail.trim(), pin: regPin,
      });
      localStorage.setItem('token', res.data.token);
      window.location.href = '/espace-client';
    } catch (err) {
      setRegError(err.response?.data?.detail || t('storefront.register.error_generic'));
    }
    setRegLoading(false);
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050a14' }}>
        <div className="text-center">
          <Store className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">{t('storefront.notfound.title')}</h1>
          <p className="text-sm text-gray-500 mb-6">{t('storefront.notfound.desc')}</p>
          <Link to="/" className="text-sm font-semibold" style={{ color: GY.green }}>{t('storefront.notfound.back')}</Link>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050a14' }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: `${GY.green}30`, borderTopColor: GY.green }} />
      </div>
    );
  }

  const statusInfo = STATUS_MAP[trackResult?.statut] || { color: '#6b7280', icon: Clock };
  const StatusIcon = statusInfo.icon;

  return (
    <div className="min-h-screen" style={{ background: '#050a14' }}>
      {/* Header */}
      <header className="border-b border-white/5" style={{ background: 'rgba(255,255,255,0.01)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${GY.green}15`, border: `1px solid ${GY.green}30` }}>
              <Store className="w-5 h-5" style={{ color: GY.green }} />
            </div>
            <div>
              <h1 className="text-base font-bold text-white" data-testid="storefront-company-name">{company.name}</h1>
              <div className="flex items-center gap-3 text-[11px] text-gray-500">
                {company.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{company.city}</span>}
                {company.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{company.phone}</span>}
              </div>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-300 transition-colors" data-testid="storefront-back-link">
            <ArrowLeft className="w-3 h-3" /> GUYAREPAIR
          </Link>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-10">
        {/* Tab selector */}
        <div className="flex mb-8 gap-2" data-testid="storefront-tabs">
          <button
            onClick={() => { setTab('track'); setLoginError(''); }}
            data-testid="storefront-tab-track"
            className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
            style={{
              background: tab === 'track' ? `${GY.green}12` : 'rgba(255,255,255,0.02)',
              border: `1px solid ${tab === 'track' ? GY.green + '40' : 'rgba(255,255,255,0.06)'}`,
              color: tab === 'track' ? GY.green : '#6b7280',
            }}
          >
            <Search className="w-4 h-4" /> {t('storefront.tab.track')}
          </button>
          <button
            onClick={() => { setTab('login'); setTrackError(''); setTrackResult(null); }}
            data-testid="storefront-tab-login"
            className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2"
            style={{
              background: tab === 'login' ? '#26c6da12' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${tab === 'login' ? '#26c6da40' : 'rgba(255,255,255,0.06)'}`,
              color: tab === 'login' ? '#26c6da' : '#6b7280',
            }}
          >
            <Lock className="w-4 h-4" /> {t('storefront.tab.login')}
          </button>
        </div>

        {/* ── Track repair tab ── */}
        {tab === 'track' && (
          <div>
            <div className="text-center mb-6">
              <h2 className="text-base font-bold text-white mb-1">{t('storefront.track.title')}</h2>
              <p className="text-xs text-gray-500">{t('storefront.track.subtitle')}</p>
              {company.repair_prefix && (
                <p className="text-[10px] text-gray-600 mt-1">{t('storefront.track.format')} <span className="font-mono" style={{ color: GY.green }}>{company.repair_prefix}XXXX</span></p>
              )}
            </div>

            <form onSubmit={handleTrack} className="flex gap-2 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                <input
                  type="text"
                  value={trackRef}
                  onChange={(e) => setTrackRef(e.target.value.toUpperCase())}
                  data-testid="storefront-track-input"
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 font-mono tracking-wider focus:outline-none"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                  placeholder={company.repair_prefix ? `${company.repair_prefix}0001` : 'REF0001'}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={trackLoading}
                data-testid="storefront-track-btn"
                className="px-5 py-3 rounded-lg text-sm font-semibold text-[#050a14] disabled:opacity-50"
                style={{ background: GY.green }}
              >
                {trackLoading ? '...' : t('storefront.track.submit')}
              </button>
            </form>

            {trackError && (
              <div className="px-4 py-3 rounded-lg text-xs text-center" data-testid="storefront-track-error"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                {trackError}
              </div>
            )}

            {trackResult && (
              <div className="rounded-xl overflow-hidden" data-testid="storefront-track-result"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-gray-500">{t('storefront.track.ref')}</span>
                    <p className="text-sm font-mono font-bold text-white">{trackResult.ref || trackResult.id}</p>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                    style={{ background: `${statusInfo.color}15`, color: statusInfo.color }}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {trackResult.statut
                      ? (STATUS_LABEL_KEY[trackResult.statut] ? t(STATUS_LABEL_KEY[trackResult.statut]) : trackResult.statut)
                      : t('storefront.status.unknown')}
                  </div>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{t('storefront.track.device')}</span>
                    <span className="text-white font-medium">{trackResult.marque} {trackResult.modele}</span>
                  </div>
                  {trackResult.probleme && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{t('storefront.track.problem')}</span>
                      <span className="text-white font-medium max-w-[200px] text-right">{trackResult.probleme}</span>
                    </div>
                  )}
                  {trackResult.date_entree && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{t('storefront.track.date_in')}</span>
                      <span className="text-white font-medium">{trackResult.date_entree}</span>
                    </div>
                  )}
                  {trackResult.date_sortie_prevue && (
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-500">{t('storefront.track.date_out')}</span>
                      <span className="text-white font-medium">{trackResult.date_sortie_prevue}</span>
                    </div>
                  )}
                  {trackResult.notes_client && (
                    <div className="mt-3 px-3 py-2 rounded-lg text-xs text-gray-400" style={{ background: 'rgba(255,255,255,0.02)' }}>
                      {trackResult.notes_client}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Client login tab ── */}
        {tab === 'login' && !showRegister && (
          <div>
            <div className="text-center mb-6">
              <h2 className="text-base font-bold text-white mb-1">{t('storefront.login.title')}</h2>
              <p className="text-xs text-gray-500">
                {t('storefront.login.subtitle').split('{{name}}').map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && <span style={{ color: '#26c6da' }}>{company.name}</span>}
                  </React.Fragment>
                ))}
              </p>
            </div>

            {loginError && (
              <div className="mb-5 px-4 py-3 rounded-lg text-xs text-center" data-testid="storefront-login-error"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                {loginError}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('storefront.login.phone')}</label>
                <div className="relative">
                  <input
                    type="tel"
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                    data-testid="storefront-phone"
                    className="w-full pl-11 pr-4 py-3.5 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    placeholder="0694 12 34 56"
                    required
                  />
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                </div>
              </div>

              {/* PIN dots */}
              <div>
                <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('storefront.login.pin')}</label>
                <div className="flex items-center justify-center gap-3 mb-4 py-2">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-3 h-3 rounded-full transition-all duration-300"
                      style={{
                        background: i < pinDigits.length ? '#26c6da' : 'rgba(255,255,255,0.06)',
                        boxShadow: i < pinDigits.length ? '0 0 8px rgba(38,198,218,0.4)' : 'none',
                        transform: i < pinDigits.length ? 'scale(1.2)' : 'scale(1)',
                      }}
                    />
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2 max-w-[260px] mx-auto">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button key={n} type="button"
                      onClick={() => { setLoginError(''); if (pinDigits.length < 6) setPinDigits(pinDigits + String(n)); }}
                      className="py-3 rounded-lg text-base font-semibold text-white transition-all duration-150 active:scale-95"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                    >{n}</button>
                  ))}
                  <button type="button" onClick={() => setPinDigits(pinDigits.slice(0, -1))}
                    className="py-3 rounded-lg text-gray-500 flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <Delete className="w-4 h-4" />
                  </button>
                  <button type="button"
                    onClick={() => { setLoginError(''); if (pinDigits.length < 6) setPinDigits(pinDigits + '0'); }}
                    className="py-3 rounded-lg text-base font-semibold text-white transition-all duration-150 active:scale-95"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>0</button>
                  <button type="button" onClick={() => setPinDigits('')}
                    className="py-3 rounded-lg text-xs font-medium text-gray-500"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>C</button>
                </div>
              </div>

              <button type="submit" disabled={loginLoading} data-testid="storefront-login-submit"
                className="w-full py-3.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-40 text-[#050a14] flex items-center justify-center gap-2"
                style={{ background: '#26c6da', boxShadow: '0 4px 20px rgba(38,198,218,0.3)' }}>
                {loginLoading ? (
                  <><div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> {t('storefront.login.loading')}</>
                ) : (
                  <><Lock className="w-4 h-4" /> {t('storefront.login.submit')}</>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button onClick={() => setShowRegister(true)} data-testid="storefront-show-register"
                className="text-xs font-medium transition-colors hover:underline" style={{ color: '#26c6da' }}>
                <UserPlus className="w-3.5 h-3.5 inline mr-1" />{t('storefront.login.create_account')}
              </button>
            </div>
          </div>
        )}

        {/* ── Client registration form ── */}
        {tab === 'login' && showRegister && (
          <div>
            <div className="text-center mb-6">
              <h2 className="text-base font-bold text-white mb-1">{t('storefront.register.title')}</h2>
              <p className="text-xs text-gray-500">
                {t('storefront.register.subtitle').split('{{name}}').map((part, i, arr) => (
                  <React.Fragment key={i}>
                    {part}
                    {i < arr.length - 1 && <span style={{ color: '#26c6da' }}>{company.name}</span>}
                  </React.Fragment>
                ))}
              </p>
            </div>

            {regError && (
              <div className="mb-5 px-4 py-3 rounded-lg text-xs text-center"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                {regError}
              </div>
            )}

            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('storefront.register.name')}</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                  <input type="text" value={regNom} onChange={(e) => setRegNom(e.target.value)} data-testid="storefront-reg-nom"
                    className="w-full pl-11 pr-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    placeholder={t('storefront.register.name_ph')} required />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('storefront.register.phone')}</label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                  <input type="tel" value={regTel} onChange={(e) => setRegTel(e.target.value)} data-testid="storefront-reg-tel"
                    className="w-full pl-11 pr-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    placeholder="0694 12 34 56" required />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('storefront.register.email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
                  <input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} data-testid="storefront-reg-email"
                    className="w-full pl-11 pr-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    placeholder={t('storefront.register.email_ph')} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('storefront.register.pin')}</label>
                  <input type="password" inputMode="numeric" maxLength={6} value={regPin} onChange={(e) => setRegPin(e.target.value.replace(/\D/g, ''))} data-testid="storefront-reg-pin"
                    className="w-full px-4 py-3 rounded-lg text-sm text-white text-center tracking-[0.3em] placeholder-gray-600 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    placeholder="••••" required />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider mb-2 font-medium text-gray-500">{t('storefront.register.pin_confirm')}</label>
                  <input type="password" inputMode="numeric" maxLength={6} value={regPin2} onChange={(e) => setRegPin2(e.target.value.replace(/\D/g, ''))} data-testid="storefront-reg-pin2"
                    className="w-full px-4 py-3 rounded-lg text-sm text-white text-center tracking-[0.3em] placeholder-gray-600 focus:outline-none"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                    placeholder="••••" required />
                </div>
              </div>
              <button type="submit" disabled={regLoading} data-testid="storefront-register-submit"
                className="w-full py-3.5 rounded-lg font-semibold text-sm transition-all disabled:opacity-40 text-[#050a14] flex items-center justify-center gap-2"
                style={{ background: '#26c6da', boxShadow: '0 4px 20px rgba(38,198,218,0.3)' }}>
                {regLoading ? t('storefront.register.loading') : <><UserPlus className="w-4 h-4" /> {t('storefront.register.submit')}</>}
              </button>
            </form>

            <div className="mt-4 text-center">
              <button onClick={() => setShowRegister(false)} data-testid="storefront-back-to-login"
                className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
                {t('storefront.register.back_to_login')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyStorefrontPage;
